import {connect} from 'react-redux';

import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AsyncSearchDropdownDataFetcher} from '../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildLensSearchUrl} from '../../../../helpers/models/lensHelpers';
import {ApiSearchLenses, LensModel} from '../../../../models/api/lensModel';
import {AppState} from '../../../../store/appState';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {ToggleDropdownLensHandler} from '../editor/context/types';
import {WorkspaceEditorLensesDropdown} from './workspaceEditorLensesDropdown';

/*
 * Props.
 */

interface WorkspaceLensesDropdownContainerProps {
  panelId: InboxesPanelId;
  onToggleDropdownLens: ToggleDropdownLensHandler;
  displayedLensesIds: Array<number>;
  lensesDropdownSelectedIds: Array<number>;
}

interface PaginationState {
  nextPageIndex: number;
}

/*
 * Selectors.
 */

function getDisplayedLensesIds(_state: AppState, props: WorkspaceLensesDropdownContainerProps) {
  return props.displayedLensesIds;
}

function getLensesDropdownSelectedIds(_state: AppState, props: WorkspaceLensesDropdownContainerProps) {
  return props.lensesDropdownSelectedIds;
}

function makeGetFetchLensesAsync() {
  return createSelector(
    [getCompanyUrl, getDisplayedLensesIds, getLensesDropdownSelectedIds],
    (
      companyUrl,
      displayedLensesIds,
      lensesDropdownSelectedIds,
    ): AsyncSearchDropdownDataFetcher<LensModel, PaginationState> =>
      async (query, lenses, cancelToken, paginationData) => {
        // Build the URL and options.
        const nextPageIndex = paginationData?.nextPageIndex || 0;
        const url = buildLensSearchUrl(companyUrl, query, {
          offset: nextPageIndex,
        });

        // Fetch the lenses.
        const fetchedLenses = await fetchApiAsync(ApiSearchLenses, url, {cancelToken});
        const canLoadMore = fetchedLenses.items.length === fetchedLenses.limit;

        // Combine the new lenses with the existing ones.
        const allLenses = lenses.concat(fetchedLenses.items);

        const allHiddenLenses = allLenses.reduce((result: Array<LensModel>, lens) => {
          if (!lens) {
            return result;
          }

          // Whether the lens was hidden before
          const isLensAlreadyHidden = !displayedLensesIds.includes(lens.id);

          // Whether the lens is currently selected in the dropdown
          const isLensCurrentlySelected = lensesDropdownSelectedIds.includes(lens.id);

          if (isLensAlreadyHidden || isLensCurrentlySelected) {
            result.push(lens);
          }
          return result;
        }, []);

        return {
          data: allHiddenLenses,
          canRequestMore: canLoadMore,
          paginationData: {
            nextPageIndex: nextPageIndex + fetchedLenses.items.length,
          },
        };
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspaceLensesDropdownContainerProps>()(() => ({
  fetchLensesAsync: makeGetFetchLensesAsync(),
}));

export const WorkspacesEditorLensesDropdownContainer = connect(
  mapStateToProps,
  {},
  (stateProps, dispatchProps, ownProps) => {
    const toggleLens = (lens: LensModel) => {
      ownProps.onToggleDropdownLens(lens);
    };

    return {
      ...stateProps,
      ...ownProps,
      toggleLens,
    };
  },
)(WorkspaceEditorLensesDropdown);
